import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Settings, Shield, Users, Truck, Wrench, ClipboardCheck, 
  Calculator, Share2, Cpu, Save, RefreshCw, Eye, EyeOff,
  Lock, Unlock, Globe, UserCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All connected apps
const APPS = [
  { app_id: 'ridero', name: 'Ridero', description: 'Delivery dispatch', icon: Truck, color: '#22c55e' },
  { app_id: 'mechanic_kiosk', name: 'MechanicKiosk', description: 'Parts ordering', icon: Wrench, color: '#3b82f6' },
  { app_id: 'pti_pector', name: 'PTI Pector', description: 'Truck inspection', icon: ClipboardCheck, color: '#f97316' },
  { app_id: 'mcanick_scout', name: 'Mcnk AI', description: 'AI Training', icon: Cpu, color: '#8b5cf6' },
  { app_id: 'mr_mathematico', name: 'Mr.Mathematico', description: 'Math learning', icon: Calculator, color: '#ec4899' },
  { app_id: 'fleet_share', name: 'Fleet Share', description: 'Fleet sharing', icon: Share2, color: '#06b6d4' },
];

// Available roles
const ROLES = [
  { role_id: 'master_admin', name: 'Master Admin' },
  { role_id: 'admin', name: 'Admin' },
  { role_id: 'moderator', name: 'Moderator' },
  { role_id: 'user', name: 'User' },
  { role_id: 'truck_driver', name: 'Truck Driver' },
  { role_id: 'ridero_driver', name: 'Ridero Driver' },
];

export default function AppSettingsPage() {
  const [appSettings, setAppSettings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, usersRes, rolesRes] = await Promise.all([
        axios.get(`${API}/app-settings`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/roles`, { withCredentials: true })
      ]);
      
      // Initialize settings for all apps
      const settings = settingsRes.data || {};
      APPS.forEach(app => {
        if (!settings[app.app_id]) {
          settings[app.app_id] = {
            enabled: true,
            visible: true,
            allowed_roles: ['master_admin', 'admin', 'moderator', 'user'],
            restricted_mode: false,
            allowed_users: [],
            url: '',
            callback_url: ''
          };
        }
      });
      
      setAppSettings(settings);
      setUsers(usersRes.data.users || []);
      setRoles(rolesRes.data || ROLES);
    } catch (error) {
      console.error('Failed to load settings:', error);
      // Initialize with defaults
      const defaultSettings = {};
      APPS.forEach(app => {
        defaultSettings[app.app_id] = {
          enabled: true,
          visible: true,
          allowed_roles: ['master_admin', 'admin', 'moderator', 'user'],
          restricted_mode: false,
          allowed_users: [],
          url: '',
          callback_url: ''
        };
      });
      setAppSettings(defaultSettings);
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/app-settings`, appSettings, { withCredentials: true });
      toast.success('App settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleAppEnabled = (appId) => {
    setAppSettings(prev => ({
      ...prev,
      [appId]: {
        ...prev[appId],
        enabled: !prev[appId]?.enabled
      }
    }));
  };

  const toggleAppVisible = (appId) => {
    setAppSettings(prev => ({
      ...prev,
      [appId]: {
        ...prev[appId],
        visible: !prev[appId]?.visible
      }
    }));
  };

  const toggleRestrictedMode = (appId) => {
    setAppSettings(prev => ({
      ...prev,
      [appId]: {
        ...prev[appId],
        restricted_mode: !prev[appId]?.restricted_mode
      }
    }));
  };

  const toggleRoleAccess = (appId, roleId) => {
    setAppSettings(prev => {
      const currentRoles = prev[appId]?.allowed_roles || [];
      const newRoles = currentRoles.includes(roleId)
        ? currentRoles.filter(r => r !== roleId)
        : [...currentRoles, roleId];
      
      return {
        ...prev,
        [appId]: {
          ...prev[appId],
          allowed_roles: newRoles
        }
      };
    });
  };

  const toggleUserAccess = (appId, userId) => {
    setAppSettings(prev => {
      const currentUsers = prev[appId]?.allowed_users || [];
      const newUsers = currentUsers.includes(userId)
        ? currentUsers.filter(u => u !== userId)
        : [...currentUsers, userId];
      
      return {
        ...prev,
        [appId]: {
          ...prev[appId],
          allowed_users: newUsers
        }
      };
    });
  };

  const openAppSettings = (app) => {
    setSelectedApp(app);
    setDialogOpen(true);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Header 
        title="App Access Settings" 
        subtitle="Control which users and roles can access each connected app" 
      />
      
      <div className="p-6 space-y-6">
        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={saveSettings} disabled={isSaving} data-testid="save-settings-btn">
            {isSaving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save All Settings
          </Button>
        </div>

        {/* Apps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {APPS.map((app) => {
            const Icon = app.icon;
            const settings = appSettings[app.app_id] || {};
            const isEnabled = settings.enabled !== false;
            const isVisible = settings.visible !== false;
            const isRestricted = settings.restricted_mode === true;
            const allowedRolesCount = (settings.allowed_roles || []).length;
            const allowedUsersCount = (settings.allowed_users || []).length;

            return (
              <Card 
                key={app.app_id}
                className={`transition-all duration-200 ${!isEnabled ? 'opacity-50' : ''}`}
                data-testid={`app-card-${app.app_id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${app.color}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: app.color }} />
                      </div>
                      <div>
                        <CardTitle className="text-base">{app.name}</CardTitle>
                        <CardDescription className="text-xs">{app.description}</CardDescription>
                      </div>
                    </div>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => toggleAppEnabled(app.app_id)}
                      data-testid={`toggle-${app.app_id}`}
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Quick Status */}
                  <div className="flex flex-wrap gap-2">
                    <Badge variant={isEnabled ? 'default' : 'secondary'}>
                      {isEnabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    <Badge variant={isVisible ? 'outline' : 'secondary'}>
                      {isVisible ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                      {isVisible ? 'Visible' : 'Hidden'}
                    </Badge>
                    <Badge variant={isRestricted ? 'destructive' : 'outline'}>
                      {isRestricted ? <Lock className="w-3 h-3 mr-1" /> : <Unlock className="w-3 h-3 mr-1" />}
                      {isRestricted ? 'Restricted' : 'Open'}
                    </Badge>
                  </div>

                  {/* Access Summary */}
                  <div className="text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      <span>{allowedRolesCount} roles allowed</span>
                    </div>
                    {isRestricted && (
                      <div className="flex items-center gap-2 mt-1">
                        <UserCheck className="w-4 h-4" />
                        <span>{allowedUsersCount} specific users</span>
                      </div>
                    )}
                  </div>

                  {/* Configure Button */}
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => openAppSettings(app)}
                    data-testid={`configure-${app.app_id}`}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Configure Access
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Reference */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Access Control Guide</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <div className="flex items-start gap-3">
              <Badge variant="default">Enabled</Badge>
              <span>App is active and can be accessed by allowed users</span>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline"><Eye className="w-3 h-3 mr-1" />Visible</Badge>
              <span>App appears in the Hub dashboard for allowed users</span>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline"><Lock className="w-3 h-3 mr-1" />Restricted</Badge>
              <span>Only specifically selected users can access (overrides role access)</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configure App Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedApp && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${selectedApp.color}20` }}
                  >
                    <selectedApp.icon className="w-5 h-5" style={{ color: selectedApp.color }} />
                  </div>
                  <div>
                    <DialogTitle>{selectedApp.name} - Access Settings</DialogTitle>
                    <DialogDescription>Configure who can access this app</DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <Tabs defaultValue="roles" className="mt-4">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="roles">Role Access</TabsTrigger>
                  <TabsTrigger value="users">User Access</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>

                {/* Role Access Tab */}
                <TabsContent value="roles" className="space-y-4 mt-4">
                  <p className="text-sm text-muted-foreground">
                    Select which roles can access {selectedApp.name}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {ROLES.map((role) => {
                      const isAllowed = (appSettings[selectedApp.app_id]?.allowed_roles || []).includes(role.role_id);
                      return (
                        <div 
                          key={role.role_id}
                          className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            isAllowed ? 'bg-primary/10 border-primary' : 'hover:bg-secondary'
                          }`}
                          onClick={() => toggleRoleAccess(selectedApp.app_id, role.role_id)}
                        >
                          <Checkbox 
                            checked={isAllowed}
                            onCheckedChange={() => toggleRoleAccess(selectedApp.app_id, role.role_id)}
                          />
                          <div>
                            <p className="font-medium text-sm">{role.name}</p>
                            <p className="text-xs text-muted-foreground">{role.role_id}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>

                {/* User Access Tab */}
                <TabsContent value="users" className="space-y-4 mt-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Restrict access to specific users only
                    </p>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="restricted-mode" className="text-sm">Restricted Mode</Label>
                      <Switch
                        id="restricted-mode"
                        checked={appSettings[selectedApp.app_id]?.restricted_mode || false}
                        onCheckedChange={() => toggleRestrictedMode(selectedApp.app_id)}
                      />
                    </div>
                  </div>

                  {appSettings[selectedApp.app_id]?.restricted_mode ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {users.map((user) => {
                        const isAllowed = (appSettings[selectedApp.app_id]?.allowed_users || []).includes(user.user_id);
                        return (
                          <div 
                            key={user.user_id}
                            className={`flex items-center space-x-3 p-2 rounded-lg border cursor-pointer transition-colors ${
                              isAllowed ? 'bg-primary/10 border-primary' : 'hover:bg-secondary'
                            }`}
                            onClick={() => toggleUserAccess(selectedApp.app_id, user.user_id)}
                          >
                            <Checkbox 
                              checked={isAllowed}
                              onCheckedChange={() => toggleUserAccess(selectedApp.app_id, user.user_id)}
                            />
                            <div className="flex-1">
                              <p className="font-medium text-sm">{user.name}</p>
                              <p className="text-xs text-muted-foreground">{user.email}</p>
                            </div>
                            <Badge variant="outline" className="text-xs">{user.role_id}</Badge>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Globe className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Restricted mode is off</p>
                      <p className="text-xs">All users with allowed roles can access this app</p>
                    </div>
                  )}
                </TabsContent>

                {/* Settings Tab */}
                <TabsContent value="settings" className="space-y-4 mt-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium text-sm">App Enabled</p>
                        <p className="text-xs text-muted-foreground">Allow SSO and access to this app</p>
                      </div>
                      <Switch
                        checked={appSettings[selectedApp.app_id]?.enabled !== false}
                        onCheckedChange={() => toggleAppEnabled(selectedApp.app_id)}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div>
                        <p className="font-medium text-sm">Visible in Hub</p>
                        <p className="text-xs text-muted-foreground">Show app icon in the Hub dashboard</p>
                      </div>
                      <Switch
                        checked={appSettings[selectedApp.app_id]?.visible !== false}
                        onCheckedChange={() => toggleAppVisible(selectedApp.app_id)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>App URL</Label>
                      <Input
                        placeholder="https://example.com"
                        value={appSettings[selectedApp.app_id]?.url || ''}
                        onChange={(e) => setAppSettings(prev => ({
                          ...prev,
                          [selectedApp.app_id]: {
                            ...prev[selectedApp.app_id],
                            url: e.target.value
                          }
                        }))}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Callback URL</Label>
                      <Input
                        placeholder="https://example.com/auth/callback"
                        value={appSettings[selectedApp.app_id]?.callback_url || ''}
                        onChange={(e) => setAppSettings(prev => ({
                          ...prev,
                          [selectedApp.app_id]: {
                            ...prev[selectedApp.app_id],
                            callback_url: e.target.value
                          }
                        }))}
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Close</Button>
                <Button onClick={saveSettings} disabled={isSaving}>
                  {isSaving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
